import React from 'react';
import { StarRating } from '../StarRating';
import { COLORS } from '../../../lib/styleGuide';

/**
 * StoreRating Component
 * 
 * Displays store rating with stars, average rating, and review count.
 * Supports different display modes for various contexts.
 * 
 * @param {Object} props
 * @param {number} props.avgRating - Average rating (0-5)
 * @param {number} props.reviewCount - Number of reviews
 * @param {string} props.size - Size of stars ('small', 'medium', 'large')
 * @param {string} props.variant - Display variant ('compact', 'detailed', 'badge')
 * @param {boolean} props.showCount - Whether to show review count
 * @param {boolean} props.showDisclaimer - Whether to show disclaimer for <3 reviews
 */
export function StoreRating({
  avgRating = 0,
  reviewCount = 0,
  size = 'medium',
  variant = 'detailed',
  showCount = true,
  showDisclaimer = true
}) {
  const formatRating = (rating) => {
    return Math.round(rating * 10) / 10;
  };
  
  const getRatingText = (rating) => {
    if (rating >= 4.5) return 'Excellent';
    if (rating >= 4.0) return 'Very Good';
    if (rating >= 3.5) return 'Good';
    if (rating >= 3.0) return 'Fair';
    if (rating >= 2.5) return 'Below Average';
    return 'Poor';
  };
  
  if (reviewCount === 0) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: COLORS.ACCENT_GRAY,
        fontSize: '14px'
      }}>
        <span>No reviews yet</span>
      </div>
    );
  }
  
  if (variant === 'compact') {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <StarRating
          rating={avgRating}
          interactive={false}
          size={size}
          showLabel={false}
        />
        <span style={{
          fontSize: '14px',
          color: COLORS.NEUTRAL_TEXT,
          fontWeight: '500'
        }}>
          {formatRating(avgRating)}
        </span>
        {showCount && (
          <span style={{
            fontSize: '12px',
            color: COLORS.ACCENT_GRAY
          }}>
            ({reviewCount})
          </span>
        )}
      </div>
    );
  }
  
  if (variant === 'badge') {
    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        background: '#F9F9F9',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        color: COLORS.NEUTRAL_TEXT
      }}>
        <span style={{ color: '#FFA500' }}>★</span>
        <span style={{ fontWeight: '500' }}>
          {formatRating(avgRating)}
        </span>
        {showCount && (
          <span style={{ color: COLORS.ACCENT_GRAY }}>
            ({reviewCount})
          </span>
        )}
      </div>
    );
  }
  
  // Detailed variant (default)
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    }}>
      <StarRating
        rating={avgRating}
        interactive={false}
        size={size}
        showLabel={false}
      />
      
      <div>
        <div style={{
          fontSize: '16px',
          fontWeight: '600',
          color: COLORS.NEUTRAL_TEXT
        }}>
          {formatRating(avgRating)} {getRatingText(avgRating)}
        </div>
        
        {showCount && (
          <div style={{
            fontSize: '14px',
            color: COLORS.ACCENT_GRAY
          }}>
            Based on {reviewCount} review{reviewCount !== 1 ? 's' : ''}
            {showDisclaimer && reviewCount < 3 && (
              <span style={{ fontStyle: 'italic' }}>
                {' '}(Based on fewer than 3 reviews)
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default StoreRating;
